import useCurrentId from '@/containers/ProjectDetail/hooks/useCurrentId';
import useHandleAllMaterials from '@/containers/ProjectDetail/hooks/useHandleAllMaterials';
import useUpdateCurrentAllMaterials from '@/containers/ProjectDetail/hooks/useUpdateCurrentAllMaterials';
import { globalInfoStore } from '@/models/global';
import { get, post } from '@/utils/request/mAxios';
import { ProjectMaterialInfo } from '@/utils/typings/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import clone from 'lodash/clone';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { GuestConversationUserInfo } from './context';

const api = {
  shareMetaData: `/tcpp/api/material/share/metadata`,
  shareList: `/tcpp/api/material/share/list`,
  addAnonymousUser: '/tcpp/api/project/im/add_anonymous_user',
  projectDetail: '/tcpp/api/project/detail'
};

interface AssetSharingMetaData {
  shareName?: string;
  orderId: string;
  folderIds: string[];
  createdAt: number;
  expiredAt: number;
  totalAssetNumber: number;
}

export const useAssetSharingMetaData = (shareToken: string) => {
  const [assetParams, setAssetParams] = useState<UseSharedAssetsListParams | null>(null);
  const { updateProjectId } = useCurrentId();

  const [guestConversationUserInfo, updateGuestConversationUserInfo] = useLocalStorage<
    GuestConversationUserInfo | undefined
  >('guest_conversation_user_info', undefined);

  // NOTE: 为啥get接口内部会改动params
  const addUsersToChat = useSharedAssetsList(clone(assetParams));

  // re-add
  useEffect(() => {
    if (!assetParams) {
      return;
    }
    addUsersToChat.refetch();
  }, [guestConversationUserInfo]);

  return useQuery(
    [api.shareMetaData, shareToken],
    () => get<AssetSharingMetaData>(api.shareMetaData, { shareToken }),
    {
      enabled: Boolean(shareToken),
      select: ({ data }) => data,
      onSuccess: async (data) => {
        const projectId = data?.orderId;
        if (projectId) {
          updateProjectId(projectId);
          const res = await globalInfoStore.fetchProjectConversationMap({ projectIds: [projectId] });
          if (res) {
            setAssetParams({ shareToken, page: 1, limit: 20 });
          }
        }
      }
    }
  );
};

interface UseAddAnonymousUserParams {
  orderId: string;
  vid: string;
  groupId: string;
  anonymousUserId: string;
}

export const useAddAnonymousUser = () => {
  return useMutation({
    mutationKey: [api.addAnonymousUser],
    mutationFn: (params: UseAddAnonymousUserParams) => {
      return post(api.addAnonymousUser, params);
    }
  });
};

interface UseSharedAssetsListParams {
  shareToken: string;
  page: number;
  limit: number;
}
export const useSharedAssetsList = (params: UseSharedAssetsListParams | null) => {
  const { mutate: addUserToConversation } = useAddAnonymousUser();
  const { projectId } = useCurrentId();
  const handleAllMaterials = useHandleAllMaterials();
  const updateCurrentAllMaterials = useUpdateCurrentAllMaterials();

  const [guestConversationUserInfo] = useLocalStorage<GuestConversationUserInfo | undefined>(
    'guest_conversation_user_info',
    undefined
  );

  return useQuery(
    [api.shareList, params],
    () => get<Record<'files', ProjectMaterialInfo[]>>(api.shareList, params),
    {
      enabled: Boolean(params),
      select: ({ data }) => data?.files || [],
      onSuccess: (data) => {
        // handleAllMaterials 接受一个三维数组
        updateCurrentAllMaterials(handleAllMaterials([[], data, []], false));
        const userId = globalInfoStore.user?.uid || guestConversationUserInfo?.conversationUserId || '';
        data.map((item) => {
          const conversationId = globalInfoStore.getConversationIdByVid(projectId || '', item.vid!);

          addUserToConversation({
            orderId: projectId,
            vid: item.vid!,
            groupId: conversationId!,
            anonymousUserId: userId
          });
        });
      }
    }
  );
};
